<template>
  <list-layout
      no-counts
      no-search
      no-apply-btn
  >
    <template v-slot:beforeFilters>
      <b-row>
        <b-col>
          <filter-builder
              :id="userPreferenceNames.PENDING_QUEUE_FILTER"
              :filters="filters"
              :apply-filters="applyFilters"
              :reset-filters="resetFilters"
              :filters-pending="filtersPending"
              :filters-applied="filtersApplied"
              @sorted="onSorted"
              @filter="onFilter"
          />
        </b-col>
      </b-row>
      <b-row class="text-center mb-3" align-v="center">
        <b-col>
          <b-form-radio-group
              v-model="pendingQueueTabs.selectedTab"
              :options="pendingQueueTabs.tabOptions"
              buttons
              button-variant="outline-secondary"
              name="radios-btn-outline"
              class="radio-buttons mr-3"
              @change="resetQueryFilters"
          />
          <b-form-radio-group
              v-model="distributionTabs.selectedAggregation"
              :options="distributionTabs.aggregationOptions"
              buttons
              button-variant="outline-secondary"
              name="radios-btn-outline"
              class="radio-buttons"
              @change="resetQueryFilters"
          />
        </b-col>
      </b-row>
    </template>

    <template v-slot:table>
      <t-table
          :key="`${pendingQueueTabs.selectedTab}-${distributionTabs.selectedAggregation}`"
          :columns="columns"
          :options="options"
          :limit="limit"
          ref="transactionsRemovalList"
          :id="getTableId"
          @loaded="onLoaded"
          @sorted="onSorted"
          @pagination="onPagination"
          @loading="onLoading"
      >
        <template v-slot:counts>
          <div class="d-flex">
            <pagination
                class="smaller p-0"
                :page="page"
                :count="count"
                :loading="loading"
                v-model="limit"
                @limit="onLimit"
            />
          </div>
        </template>
        <template v-slot:merge>
          <b-button v-if="canExecutePayout()" @click="onExecute" class="btn-outline-red">
            {{ $t("execute") | capitalize }}
          </b-button>
        </template>

        <!-- Requester -->
        <template v-slot:createdBy="{ row }">
          {{ row.createdBy?.fullName }}
        </template>
        <!-- Rightsholder ID -->
        <template v-slot:rightsHolderId="{ row }">
          <database-link
              :type="linkTypes.RIGHTS_HOLDER"
              :item-id="row.rightsHolder?.id"
          >
            {{ row.rightsHolder?.resoundId }}
          </database-link>
        </template>
        <!-- Rightsholder Name -->
        <template v-slot:rightsHolderName="{ row }">
          {{ row.rightsHolder?.name }}
        </template>
        <!-- Reviewer -->
        <template v-slot:reviewer="{ row }">
          {{ row.reviewer?.fullName }}
        </template>
        <!-- Execution Date -->
        <template v-slot:executionDate="{ row }">
          {{ row.executionDate }}
        </template>
        <!-- Reason -->
        <template v-slot:reason="{ row }">
          {{ $t(row.reason) }}
        </template>
        <!-- Comments -->
        <template v-slot:comments="{ row }">
          <div v-for="(item, key) in row.comments" :key="key" class="PendingQueueSection__comment d-flex">
            <div>
              <fa-icon size="lg" :icon="['far','comment-lines']"/> 
            </div>
            <div class="pl-3">
              <span class="font-weight-bold">
                {{ parseDate(item.created, $config.DATE_FORMAT) }} {{ parseDate(item.created, $config.TIME_FORMAT) }}
              </span>
              -
              {{ 
              `${item.createdBy.firstName}
              ${item.createdBy.lastName}
              (${item.createdBy.memberCollective ? item.createdBy.memberCollective : item.createdBy.daAgent})
              ${$t("wrote")}:` 
              }}
              <div>
                {{ item.comment }}
              </div>
            </div>
          </div>
        </template>
        <!-- Recording ID -->
        <template v-slot:recordingId="{ row }">
          <database-link
              :type="linkTypes.RECORDING"
              :item-id="row.recording?.id"
          >
            {{ row.recording?.resoundId }}
          </database-link>
        </template>
        <!-- Artist -->
        <template v-slot:recordingArtist="{ row }">
          {{ row.recording?.artist }}
        </template>
        <!-- Title -->
        <template v-slot:recordingTitle="{ row }">
          {{ row.recording?.title }}
        </template>
        <!-- Type -->
        <template v-slot:type="{ row }">
          {{ capitalize(row.rightsHolder?.type) }}
        </template>

        <!-- Status -->
        <template v-slot:status="{ row }">
          <div v-if="row.status === transactionRemovalStatuses.APPROVED && pendingQueueTabs.selectedTab === 'removal'">
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.PENDING"
                :text="capitalize($t('removal_queued'))"
                name="removal_queued"
                :icon="['fas', 'question-circle']"
                color="orange"
            />
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.IN_PROGRESS"
                :text="capitalize($t('removal_progress'))"
                name="removal_progress"
                :icon="['fas', 'question-circle']"
                color="orange"
            />
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.COMPLETED"
                :text="capitalize($t('removed'))"
                name="removed"
                :icon="['fas', 'check-circle']"
                color="green"
            />
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.ERROR"
                name="removal_denied"
                :text="capitalize($t('removal_denied'))"
                :icon="['fas', 'exclamation-circle']"
                color="red"
            />
          </div>
          <div v-else-if="pendingQueueTabs.selectedTab === 'removal'">
            <status
                v-if="row.status === transactionRemovalStatuses.IN_PROGRESS || row.status === transactionRemovalStatuses.PENDING"
                :text="capitalize($t('removal_requested'))"
                name="removal_requested"
                :icon="['fas', 'question-circle']"
                color="orange"
            />
            <status
                v-if="row.status === transactionRemovalStatuses.REJECTED"
                name="removal_denied"
                :text="capitalize($t('removal_denied'))"
                :icon="['fas', 'exclamation-circle']"
                color="red"
            />
          </div>
          <!-- Status Transacation Refresh -->
          <div v-else>
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.COMPLETED"
                :text="capitalize($t('refreshed'))"
                name="refreshed"
                :icon="['fas', 'check-circle']"
                color="green"
            />
            <status
                v-else-if="row.executionStatus === payoutJobExecutionStatuses.IN_PROGRESS"
                :text="capitalize($t('refresh_progress'))"
                name="refresh_progress"
                :icon="['fas', 'question-circle']"
                color="orange"
            />
            <status
                v-else-if="row.executionStatus === payoutJobExecutionStatuses.ERROR"
                name="refresh_failed"
                :text="capitalize($t('refresh_failed'))"
                :icon="['fas', 'exclamation-circle']"
                color="red"
            />
            <status
                v-if="row.executionStatus === payoutJobExecutionStatuses.PENDING"
                :text="capitalize($t('refresh_queued'))"
                name="refresh_queued"
                :icon="['fas', 'question-circle']"
                color="orange"
            />
          </div>

        </template>

        <!-- Action -->
        <template v-slot:action="{ row }" v-if="permissions.pages[Pages.TransactionsRemovalList].canWrite">
          <b-button v-if="row.status === transactionRemovalStatuses.PENDING" @click="onDeny(row.id)"
                    class="btn-outline-dark mr-3">
            {{ $t("deny") | capitalize }}
          </b-button>
          <b-button v-if="row.status === transactionRemovalStatuses.PENDING" @click="onApprove(row.id)"
                    class="btn-outline-red">
            {{ $t("approve") | capitalize }}
          </b-button>

          <modal
              :id="`deny-modal-${row.id}`"
              modal-type="error"
              centered
              size="lg"
              hide-footer
              noCloseOnBackdrop
              noCloseOnEsc
              :custom-title="$t('deny_transaction_removal') | capitalize"
              @cancel="cancelDeny(row.id)"
          >
            {{ denyMessage(row) }}
            <b-form @submit.prevent="confirmDeny(row)">
              <b-form-group>
                <b-form-textarea
                    size="lg"
                    class="mb-4 mt-4"
                    debounce="500"
                    name="comment"
                    :placeholder="$t('make_comment') | capitalize"
                    v-model="denialComment"
                    rows="5"
                />
              </b-form-group>
              <b-row class="modal-footer padding">
                <b-button @click="cancelDeny(row.id)" class="btn-outline-dark">
                  {{ $t("cancel") | capitalize }}
                </b-button>
                <b-button type="submit" :disabled="!denialComment" class="btn-outline-red">
                  {{ $t("confirm") | capitalize }}
                </b-button>
              </b-row>
            </b-form>
          </modal>

          <modal
              :id="`approve-modal-${row.id}`"
              :cancel-title="$t('cancel') | capitalize"
              :ok-title="$t('confirm') | capitalize"
              @ok="confirmApprove(row)"
              @cancel="$bvModal.hide(`approve-modal-${row.id}`)"
              @close="$bvModal.hide(`approve-modal-${row.id}`)"
              modal-type="info"
              centered
              size="lg"
              :custom-title="$t('approve_transactional_removal') | capitalize"
          >
            {{ approvalMessage(row) }}
          </modal>
        </template>
      </t-table>
    </template>
    <div class="fixed-footer">
      <div class="d-flex justify-content-end">
        <b-button class="mr-3 btn-outline-dark" @click="prevTab">
          {{ $t("go_back") | capitalize }}
        </b-button>
        <b-button v-if="canEdit" class="btn-outline-red mr-3" @click="moveToScope">
          {{ $t("modify_scope") | capitalize }}
        </b-button>
      </div>
    </div>

    <modal
        id="execute-modal"
        :cancel-title="$t('cancel') | capitalize"
        :ok-title="$t('confirm') | capitalize"
        @ok="confirmExecute()"
        @cancel="$bvModal.hide('execute-modal')"
        modal-type="info"
        centered
        size="lg"
        :custom-title="$t('approve_transactional_removal') | capitalize"
    >
      {{ executeJobMessage }}
    </modal>
  </list-layout>
</template>

<script>
import { capitalize, get, snakeCase, upperCase } from "lodash"
import {
  filterTypes,
  linkTypes,
  payoutJobExecutionStatuses,
  payoutJobTypes,
  payoutTabs,
  pendingQueueAggregations,
  perPageItemsLg,
  rightsholderTypes,
  transactionRefreshStatuses,
  transactionRemovalStatuses,
  userPreferenceNames,
} from "@/constants"
import { mapActions, mapState } from "vuex"
import DatabaseLink from "@/components/DatabaseLink.vue"
import FilterBuilder from "@/components/FilterBuilder"
import { Pages } from "@/utils/pages"
import Pagination from "@/components/Pagination"
import { listRouteMixin } from "@/utils/mixins"
import parseDate from "@/utils/date-parser"

export default {
  name: "PendingQueueSection",
  mixins: [listRouteMixin],
  components: { FilterBuilder, Pagination, DatabaseLink },
  props: {
    payoutId: String,
    canEdit: Boolean,
  },
  data () {
    return {
      page: 1,
      count: 0,
      limit: perPageItemsLg,
      loading: false,
      userPreferenceNames,
      capitalize,
      Pages,
      denialComment: "",
      payoutJobExecutionStatuses,
      transactionRemovalStatuses,
      linkTypes,
      pendingQueueTabs: {
        tabOptions: [
          {
            value: "removal",
            text: capitalize(this.$t("removal")),
          },
          {
            value: "refresh",
            text: capitalize(this.$t("refresh")),
          }
        ],
        selectedTab: "removal"
      },
      distributionTabs: {
        aggregationOptions: [
          {
            value: "rightsHolder",
            text: this.$t("rights_holder_title"),
          },
          {
            value: "rightsHolderTariffRecording",
            text: this.$t("rights_holder_tariff_recording"),
          },
        ],
        selectedAggregation: "rightsHolder",
      },
      filters: {
        id: { value: "", defaultValue: "", type: filterTypes.TEXT, label: upperCase(this.$t("id")), hidden: true },
        description: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("tariff_period")),
          hidden: false
        },
        recordingArtist: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("artist")),
          hidden: false
        },
        collectiveSociety: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("collective_society")),
          hidden: false
        },
        amount: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("net_amount")),
          hidden: false
        },
        playcount: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("playcount")),
          type: filterTypes.NUMBER_RANGE_NO_SLIDER,
        },
        recording: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("recording")),
          hidden: true
        },
        rightsholder: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("rightsholder")),
          hidden: true
        },
        share: { value: "", defaultValue: "", type: filterTypes.TEXT, label: capitalize(this.$t("share")), hidden: true },
        payee: { value: "", defaultValue: "", type: filterTypes.TEXT, label: capitalize(this.$t("payee")), hidden: true },
        status: {
          value: "",
          defaultValue: "",
          type: filterTypes.SELECT,
          options: [],
          label: capitalize(this.$t("status")),
          hidden: true
        },
        removal_status: {
          value: "",
          defaultValue: "",
          type: filterTypes.SELECT,
          options: [],
          label: capitalize(this.$t("status")),
          hidden: true
        },
        reviewer: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          listEndpoint: this.$api.users.usersSearch,
          excludeName: "id",
          textField: "name",
          valueField: "email",
          label: capitalize(this.$t("reviewer")),
          placeholder: capitalize(this.$t("search_user")),
          searchName: "search",
        },
        requester: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          listEndpoint: this.$api.users.usersSearch,
          excludeName: "id",
          textField: "name",
          valueField: "email",
          label: capitalize(this.$t("requester")),
          placeholder: capitalize(this.$t("search_user")),
          searchName: "search",
        },
        type: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("type")),
          options: this.rightsHolderType(),
          type: filterTypes.SELECT,
          hidden: false
        },
        tariffPeriodYear: {
          value: "",
          defaultValue: "",
          type: filterTypes.TEXT,
          label: capitalize(this.$t("tariff_period_year")),
          hidden: false
        },
        requestDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("request_date")),
          noWatch: true,
          hidden: false
        },
        executionDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("execution_date")),
          noWatch: true,
          hidden: false
        },
        rightsHolder: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("rights_holder")),
          placeholder: capitalize(this.$t("search_rightsholder")),
          excludeName: "id",
          valueField: "resoundId",
          textField: "name",
          searchName: "search",
          listEndpoint: this.$api.rightsholders.rightsholdersList,
          noWatch: true,
        },
        title: {
          value: null,
          defaultValue: null,
          type: filterTypes.TEXT,
          label: capitalize(this.$t("title"))
        },
      },
      options: {
        sortable: [
          "createdBy",
          "createdAt",
          "rightsHolderId",
          "rightsHolderName",
          "payeeName",
          "societyName",
          "amount",
          "transactionsCount",
          "status",
          "reviewer",
          "executionDate",
          "distributionDescription",
          "recordingId",
          "recordingArtist",
          "recordingTitle",
          "type",
          "playcount",
          "share",
          "reason",
          "comments",
        ],
        headings: {
          createdBy: capitalize(this.$t("requester")),
          createdAt: capitalize(this.$t("request_date")),
          rightsHolderId: capitalize(this.$t("rights_holder_id")),
          rightsHolderName: capitalize(this.$t("rights_holder_name")),
          payeeName: capitalize(this.$t("payee_name")),
          societyName: capitalize(this.$t("collective_society")),
          amount: capitalize(this.$t("net_amount")),
          transactionsCount: this.$t("transactions_count"),
          status: capitalize(this.$t("status")),
          reviewer: capitalize(this.$t("reviewer")),
          executionDate: capitalize(this.$t("execution_date")),
          distributionDescription: capitalize(this.$t("tariff_periods")),
          recordingId: this.$t("recording_id"),
          recordingArtist: capitalize(this.$t("artist")),
          recordingTitle: capitalize(this.$t("title")),
          type: capitalize(this.$t("type")),
          playcount: capitalize(this.$t("total_playcount")),
          share: capitalize(this.$t("share")),
          reason: capitalize(this.$t("reason")),
          comments: capitalize(this.$t("comments")),
          action: "",
        },
        width: {
          createdBy: 150,
          createdAt: 150,
          status: 150,
          action: 150,
          comments: 150,
        },
        responseAdapter ({ data }) {
          return {
            data: data.results,
            count: data.count,
          }
        },
        requestFunction (queryParams) {
          queryParams = { ...queryParams, ...this.$route.query }
          this.page = Number(get(queryParams, "page", 1))
          queryParams.aggregation_level = pendingQueueAggregations[queryParams.aggregation_level] || pendingQueueAggregations.rightsHolder
          queryParams.payout_id = this.$parent.$parent.payoutId
          const selectedTab = this.$parent.$parent.pendingQueueTabs.selectedTab
          return {
            "removal": this.$api.payouts.getTransactionsRemovalList,
            "refresh": this.$api.payouts.getTransactionsRefreshList
          }[selectedTab](queryParams)
        },
      }
    }
  },
  mounted () {
    this.distributionTabs.selectedAggregation = this.$route.query["aggregation_level"] || "rightsHolder"
    this.filtersWithOptions()
    this.setStatusOptions()
    this.resetQueryFilters()
  },
  computed: {
    ...mapState("user", ["permissions"]),
    getTableId () {
      return `payout-pending-queue-list-${this.pendingQueueTabs.selectedTab}-${this.distributionTabs.selectedAggregation}`
    },
    executeJobMessage () {
      return capitalize(
        this.pendingQueueTabs.selectedTab === "removal" ? this.$t("execute_transaction_removals") : this.$t("execute_transaction_refreshes")
      )
    },
    columns () {
      let columnsSet
      switch (this.distributionTabs.selectedAggregation) {
      case "rightsHolder":
        columnsSet = [
          "createdBy",
          "createdAt",
          "rightsHolderId",
          "rightsHolderName",
          "payeeName",
          "societyName",
          "amount",
          "transactionsCount",
          "status",
          "executionDate",
        ]
        break
      case "rightsHolderTariffRecording":
        columnsSet = [
          "createdBy",
          "createdAt",
          "distributionDescription",
          "recordingId",
          "recordingArtist",
          "recordingTitle",
          "type",
          "rightsHolderId",
          "rightsHolderName",
          "payeeName",
          "societyName",
          "playcount",
          "share",
          "amount",
          "status",
          "executionDate",
        ]
        break
      default:
        columnsSet = [
          "createdBy",
          "createdAt",
          "rightsHolderId",
          "rightsHolderName",
          "payeeName",
          "societyName",
          "amount",
          "transactionsCount",
          "status",
          "executionDate",
          "comments",
        ]
      }
      if (this.pendingQueueTabs.selectedTab === "removal") {
        columnsSet.push("action")
        columnsSet.push("reviewer")
        columnsSet.push("reason")
        columnsSet.push("comments")
      }
      return columnsSet
    },
  },
  methods: {
    ...mapActions("alert", ["success", "error"]),
    parseDate,
    onLoaded ({ data }) {
      this.count = data?.count || 0
      this.summary = data?.summary || {}
      this.loading = false
    },
    getData () {
      this.$refs.transactionsRemovalList.getData()
    },
    
    canExecutePayout () {
      return this.permissions.actions.canExecutePayoutJob
    },
    onExecute () {
      this.$bvModal.show("execute-modal")
    },
    confirmExecute () {
      let jobType
      let successMsg
      let failMsg
      if (this.pendingQueueTabs.selectedTab === "removal") {
        jobType = payoutJobTypes.removal
        successMsg = this.$t("removal_jobs_success")
        failMsg = this.$t("removal_jobs_fail")
      } else {
        jobType = payoutJobTypes.refresh
        successMsg = this.$t("refresh_jobs_success")
        failMsg = this.$t("refresh_jobs_fail")
      }
      let data = {
        payoutId: this.payoutId,
        type: jobType
      }
      this.$api.payouts.executePayoutJob(data).then(() => {
        this.$bvModal.hide("execute-modal")
        this.getData()
        this.success(capitalize(successMsg))
      }).catch((error) => {
        const { status } = error.response
        if (status !== 500) {
          this.error(capitalize(this.$t(failMsg)))
        }
      })
    },
    onApprove (id) {
      this.$bvModal.show(`approve-modal-${id}`)
    },
    confirmApprove (transaction) {
      this.$api.payouts.approveTransactionRemovalRequest(transaction.id).then(() => {
        this.getData()
        this.$bvModal.hide(`approve-modal-${transaction.id}`)
        this.success(`Removal request for ${transaction?.rightsHolder?.resoundId} for ${transaction?.rightsHolder?.name}, with ${transaction?.transactionsCount} transactions and ${transaction?.amount} net amount has been approved.`)
      }).catch(() => {
        let msg = "Failed to approve transaction. Please contact administrator or try again."
        this.error(msg)
      })
    },
    approvalMessage (transaction) {
      return `You are about to approve the request to remove ${transaction?.rightsHolder?.resoundId} for ${transaction?.rightsHolder?.name}, with ${transaction?.transactionsCount} transactions and ${transaction?.amount} net amount. Are you sure you want to proceed?`
    },
    onDeny (id) {
      this.$bvModal.show(`deny-modal-${id}`)
    },
    confirmDeny (transaction) {
      let data = {
        comment: this.denialComment
      }
      this.$api.payouts.declineTransactionRemovalRequest(transaction.id, data).then(() => {
        this.getData()
        this.$bvModal.hide(`deny-modal-${transaction.id}`)
        this.success(`Removal request for ${transaction?.rightsHolder?.resoundId} for ${transaction?.rightsHolder?.name}, with ${transaction?.transactionsCount} transactions and ${transaction?.amount} net amount has been denied.`)
        this.denialComment = ""
      }).catch(() => {
        let msg = "Failed to deny transaction. Please contact administrator or try again."
        this.error(msg)
      })
    },
    cancelDeny (id) {
      this.$bvModal.hide(`deny-modal-${id}`)
      this.denialComment = ""
    },
    denyMessage (transaction) {
      return `You are about to deny the request to remove ${transaction?.rightsHolder?.resoundId} for ${transaction?.rightsHolder?.name}, with ${transaction?.transactionsCount} transactions and ${transaction?.amount} net amount. Please provide a comment explaining your decision.`
    },
    prevTab () {
      this.$parent.$parent.previousTab()
    },
    moveToScope () {
      const tabs = this.$parent.$parent.$parent.$parent.sections
      const scopeTabIndex = tabs.findIndex(tab => tab.type === payoutTabs.SCOPE)
      this.$parent.$parent.$parent.$parent.tabIndex = scopeTabIndex
    },
    onFilter (name, value){
      if (name === "requestDate") name = "createdAt"
      if (["createdAt", "executionDate"].includes(name)) {
        if (value !== null && value.length) {
          const range = (value) ?
            [
              this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
              this.parseDate(value[1], this.$config.ISO_DATE_FORMAT)
            ] : []
          this.changeFilter(`${name}_min`, range[0])
          this.changeFilter(`${name}_max`, range[1])
        } else {
          delete this.queryParams[snakeCase(name) +  "_min"]
          delete this.queryParams[snakeCase(name) +  "_max"]
        }
        
      }
      else if (name === "requester" || name === "reviewer") {
        if (value === null) {
          this.changeFilter(name, value)
        } else {
          this.changeFilter(name, value.value)
        }
      }
    },
    setStatusOptions () {
      var options = []

      if (this.pendingQueueTabs && this.pendingQueueTabs.selectedTab === "removal") {
        options = [
          {
            text: capitalize(this.$t("removal_requested")),
            value: transactionRemovalStatuses.IN_PROGRESS
          },
          {
            text: capitalize(this.$t("removal_denied")),
            value: transactionRemovalStatuses.REJECTED
          }
        ]
        this.filters.removal_status.options = options
      }
      else {
        options = [
          {
            text: capitalize(this.$t("completed")),
            value: transactionRefreshStatuses.COMPLETED
          },
          {
            text: capitalize(this.$t("in_progress")),
            value: transactionRefreshStatuses.IN_PROGRESS
          },
          {
            text: capitalize(this.$t("pending")),
            value: transactionRefreshStatuses.PENDING
          },
          {
            text: capitalize(this.$t("error")),
            value: transactionRefreshStatuses.ERROR
          }
        ]
        this.filters.status.options = options
      }
    },
    rightsHolderType (){
      return [
        {
          text: capitalize(rightsholderTypes.PERFORMER),
          value: rightsholderTypes.PERFORMER
        },
        {
          text: capitalize(rightsholderTypes.MAKER),
          value: rightsholderTypes.MAKER
        }
      ]
    },
    filtersWithOptions () {
      let selectedFilters = []
      if (this.pendingQueueTabs.selectedTab === "removal") {
        switch (this.distributionTabs.selectedAggregation) {
        case "rightsHolder":
          selectedFilters = [
            "id",
            "description",
            "amount",
            "collectiveSociety",
            "rightsHolder",
            "payee",
            "removal_status",
            "reviewer",
            "requester",
            "requestDate",
            "executionDate",
          ]
          break
        case "rightsHolderTariffRecording":
          selectedFilters = [
            "id",
            "description",
            "amount",
            "collectiveSociety",
            "rightsHolder",
            "payee",
            "removal_status",
            "reviewer",
            "requester",
            "requestDate",
            "executionDate",
            "recording",
            "recordingArtist",
            "title",
            "type",
          ]
          break
        default:
          selectedFilters = ["id", "amount", "description"]
        }
      } else {
        switch (this.distributionTabs.selectedAggregation) {
        case "rightsHolder":
          selectedFilters = [
            "amount",
            "collectiveSociety",
            "rightsHolder",
            "payee",
            "status",
            "requester",
            "requestDate",
            "executionDate"
          ]
          break
        case "rightsHolderTariffRecording":
          selectedFilters = [
            "amount",
            "recordingArtist",
            "collectiveSociety",
            "playcount",
            "recording",
            "rightsHolder",
            "tariff",
            "description",
            "tariffPeriodYear",
            "share",
            "payee",
            "status",
            "type",
            "requester",
            "requestDate",
            "executionDate",
            "title"
          ]
          break
        default:
          selectedFilters = ["id", "amount", "description"]
        }
      }
      this.filters = Object.fromEntries(
        Object.entries(this.filters).map(([key, filter]) => [
          key,
          { ...filter, hidden: !selectedFilters.includes(key) }
        ])
      )
    },
    resetQueryFilters () {
      this.resetFilters(true)
    }
  },
  watch: {
    "pendingQueueTabs.selectedTab": function () {
      this.getData()
      this.filtersWithOptions()
      this.setStatusOptions()
    },
    "distributionTabs.selectedAggregation": function (value) {
      this.queryParams = {
        ...this.queryParams, "aggregation_level": value
      }
      this.changeFilter("aggregationLevel", value, true)
      this.filtersWithOptions()
    }
  },
}
</script>
<style lang="scss" scoped>
.padding {
  padding-left: 1rem;
  padding-right: 1rem;
}

.PendingQueueSection__comment:not(:last-child) {
  padding-bottom: 1rem;
}
</style>